import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt'
import { alpha, Box, Button, Divider, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import SharedSelect from 'components/shared/Select/SharedSelect'
import React from 'react'
import VersionChip from 'theme/components/Chip/VersionChip'
import { ProductSortMode } from 'types'
import ContractActionsMenu from './ContractActionsMenu'
import HeartbeatAccountUsed from './HeartbeatAccountUsed'
import TemplateUsed from './TemplateUsed'

type ContractToolbarProps = {
  // Sort props
  sortMode: ProductSortMode
  onSortChange: (mode: ProductSortMode) => void
  // Status props
  isEditing: boolean
  isDirty: boolean
  productsDirty: boolean
  isSaveRequired: boolean
  // Action props
  ok: boolean
  deal: any
  productRows: any[]
  productChanges: any
  activeUser: any
  onSaveAll: () => void
  onSaveAndUpdate: (e: React.MouseEvent<HTMLButtonElement>) => void
  onCancel: () => void
  onEdit: () => void
  onRequest: () => void
  onCreate: (e: React.MouseEvent<HTMLButtonElement>) => void
  isRequesting: boolean
  isCreating: boolean
  validationErrors: any
  resetDealBaseline: () => void
  disableSaveAndUpdate?: boolean
  /** Short/narrow viewport (e.g. 150% browser zoom): one slim row with a
   *  label-less sort, no template/Heartbeat chips */
  compact?: boolean
}

const ContractToolbar: React.FC<ContractToolbarProps> = ({
  sortMode,
  onSortChange,
  isEditing,
  isDirty,
  productsDirty,
  isSaveRequired,
  ok,
  deal,
  productRows,
  productChanges,
  activeUser,
  onSaveAll,
  onSaveAndUpdate,
  onCancel,
  onEdit,
  onRequest,
  onCreate,
  isRequesting,
  isCreating,
  validationErrors,
  resetDealBaseline,
  disableSaveAndUpdate = false,
  compact = false,
}) => {
  const theme = useTheme()
  const primaryAlpha = (opacity: number) => alpha(theme.palette.primary.main, opacity)
  const errorAlpha = (opacity: number) => alpha(theme.palette.error.main, opacity)

  const sortOptions = [
    { value: 'SAVED_ORDER', label: 'Saved Order' },
    { value: 'NAME_CATEGORY_ASC', label: 'Name, then Category' },
    { value: 'NAME_ASC', label: 'Name (A-Z)' },
    { value: 'NAME_DESC', label: 'Name (Z-A)' },
    { value: 'DATE_ASC', label: 'Date (Earliest First)' },
    { value: 'DATE_DESC', label: 'Date (Latest First)' },
    { value: 'STATUS_ACTIVE_FIRST', label: 'Status (Active First)' },
    { value: 'STATUS_ACTIVE_LAST', label: 'Status (Active Last)' },
    { value: 'PRICE_ASC', label: 'Price (Low to High)' },
    { value: 'PRICE_DESC', label: 'Price (High to Low)' },
    { value: 'DIGITAL_SUBCATEGORY_FIRST', label: 'Category (Digital First)' },
    { value: 'CATEGORY_ASC', label: 'Category (A-Z)' },
    { value: 'CATEGORY_DESC', label: 'Category (Z-A)' },
    { value: 'SUBCATEGORY_ASC', label: 'Subcategory (A-Z)' },
    { value: 'SUBCATEGORY_DESC', label: 'Subcategory (Z-A)' },
  ]

  const buttonSx = {
    textTransform: 'none' as const,
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 2,
    px: 1.5,
    py: 0.5,
    minHeight: 34,
    lineHeight: 1.2,
  }

  return (
    <Box
      sx={{
        bgcolor: alpha(theme.palette.text.primary, 0.02),
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px 12px 0 0',
        px: 2,
        py: compact ? 0.5 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // Narrow column (small screen, browser zoom, sidebar open): the actions
        // drop to a second row instead of squeezing into a one-button-wide
        // column that ate the product table's height.
        flexWrap: compact ? 'nowrap' : 'wrap',
        gap: 2,
        rowGap: 1,
      }}
    >
      {/* Left: Version + Sort + which paper this deal uses + the Heartbeat account it reaches */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, minWidth: 0 }}>
        {deal?.semanticVersion && <VersionChip version={deal?.semanticVersion} />}
        <SharedSelect
          id={'toolbar-sort-label'}
          label={compact ? undefined : 'Sort By'}
          value={sortMode}
          options={sortOptions}
          onChange={e => onSortChange(e.target.value as ProductSortMode)}
          renderValue={
            compact
              ? selected => (
                  <Typography sx={{ fontSize: 13 }} noWrap>
                    Sort: {sortOptions.find(opt => opt.value === selected)?.label ?? String(selected ?? '')}
                  </Typography>
                )
              : undefined
          }
        />
        {!compact && <TemplateUsed deal={deal} />}
        {!compact && <HeartbeatAccountUsed deal={deal} />}
      </Box>

      {/* Right: Action Buttons */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        sx={{ rowGap: 0.5, flexShrink: 0, ml: 'auto' }}
      >
        {isEditing ? (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon sx={{ fontSize: '18px !important' }} />}
              onClick={onCancel}
              sx={{
                ...buttonSx,
                color: 'text.secondary',
                borderColor: alpha(theme.palette.text.primary, 0.15),
                '&:hover': {
                  borderColor: 'error.main',
                  color: 'error.main',
                  bgcolor: errorAlpha(0.06),
                },
              }}
            >
              Cancel
            </Button>
            {deal?.contractId ? (
              <Tooltip
                title={
                  <Typography sx={{ fontSize: 13 }}>
                    {disableSaveAndUpdate
                      ? 'Loop services added — a new deal (Major revision) is required'
                      : !isSaveRequired
                      ? 'No changes to save'
                      : 'Save & Update Contract'}
                  </Typography>
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SystemUpdateAltIcon sx={{ fontSize: '18px !important' }} />}
                    onClick={onSaveAndUpdate}
                    disabled={!isSaveRequired || disableSaveAndUpdate}
                    sx={{
                      ...buttonSx,
                      bgcolor: isSaveRequired ? 'primary.main' : undefined,
                      ...(isSaveRequired && {
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { boxShadow: '0 0 0 0 rgba(52,152,219,0.4)' },
                          '50%': { boxShadow: '0 0 0 6px rgba(52,152,219,0)' },
                        },
                      }),
                    }}
                  >
                    Save & Update
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Tooltip
                title={
                  <Typography sx={{ fontSize: 13 }}>
                    {!isSaveRequired ? 'No changes to save' : 'Save Agreement'}
                  </Typography>
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon sx={{ fontSize: '18px !important' }} />}
                    onClick={onSaveAll}
                    disabled={!isSaveRequired}
                    sx={{
                      ...buttonSx,
                      bgcolor: isSaveRequired ? 'primary.main' : undefined,
                      ...(isSaveRequired && {
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { boxShadow: '0 0 0 0 rgba(52,152,219,0.4)' },
                          '50%': { boxShadow: '0 0 0 6px rgba(52,152,219,0)' },
                        },
                      }),
                    }}
                  >
                    Save
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        ) : (
          <>
            <Tooltip
              title={
                <Typography sx={{ fontSize: 13 }}>
                  {!deal?.isDraft ? 'Cannot edit (locked)' : 'Edit Agreement'}
                </Typography>
              }
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon sx={{ fontSize: '18px !important' }} />}
                  onClick={onEdit}
                  disabled={!deal?.isDraft}
                  sx={{
                    ...buttonSx,
                    borderColor: alpha(theme.palette.text.primary, 0.15),
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      bgcolor: primaryAlpha(0.06),
                    },
                  }}
                >
                  Edit
                </Button>
              </span>
            </Tooltip>
            {isSaveRequired && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                {deal?.contractId ? (
                  <Tooltip
                    title={
                      disableSaveAndUpdate ? (
                        <Typography sx={{ fontSize: 13 }}>
                          Loop services added — a new deal (Major revision) is required
                        </Typography>
                      ) : undefined
                    }
                  >
                  <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SystemUpdateAltIcon sx={{ fontSize: '18px !important' }} />}
                    onClick={onSaveAndUpdate}
                    disabled={disableSaveAndUpdate}
                    sx={{
                      ...buttonSx,
                      ...(!disableSaveAndUpdate && {
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { boxShadow: `0 0 0 0 ${primaryAlpha(0.4)}` },
                          '50%': { boxShadow: `0 0 0 6px ${primaryAlpha(0)}` },
                        },
                      }),
                    }}
                  >
                    Save & Update
                  </Button>
                  </span>
                  </Tooltip>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon sx={{ fontSize: '18px !important' }} />}
                    onClick={onSaveAll}
                    sx={{
                      ...buttonSx,
                      animation: 'pulse 2s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { boxShadow: `0 0 0 0 ${primaryAlpha(0.4)}` },
                        '50%': { boxShadow: `0 0 0 6px ${primaryAlpha(0)}` },
                      },
                    }}
                  >
                    Save
                  </Button>
                )}
              </>
            )}
          </>
        )}

        {!isEditing && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <ContractActionsMenu
              deal={deal}
              productRows={productRows}
              productChanges={productChanges}
              activeUser={activeUser}
              isRequesting={isRequesting}
              onRequestApproval={onRequest}
              onCreate={onCreate}
              validationErrors={validationErrors}
              isCreating={isCreating}
              isValid={ok ?? false}
              isDisabled={!ok}
              resetDealBaseline={resetDealBaseline}
              isSaveRequired={isSaveRequired}
              direction="row"
              spacing={1}
              sizeConfig={{
                buttonSize: 40,
                iconSize: 32,
                muiIconSize: 32,
                badgeSize: 18,
              }}
              labeledMode
            />
          </>
        )}
      </Stack>
    </Box>
  )
}

export default ContractToolbar
